import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  STRRule,
  STRAlert,
  STRAccountMonitoring,
  STRDashboardStats,
  STRDailySummary,
  STRAccountFlags,
} from '../../services/strService/strService';
import {
  getSTRDashboardStatsThunk,
  getSTRDailySummaryThunk,
  getSTRWeeklySummaryThunk,
  getAllSTRRulesThunk,
  getSTRRuleThunk,
  updateSTRRuleThresholdsThunk,
  toggleSTRRuleThunk,
  getSTRAlertsThunk,
  hideSTRAlertThunk,
  showSTRAlertThunk,
  resolveSTRAlertThunk,
  bulkHideSTRAlertsThunk,
  hideAllAccountSTRAlertsThunk,
  hideRuleSTRAlertsForAccountThunk,
  getSTRAlertStatisticsThunk,
  getSTRAccountMonitoringStatusThunk,
  searchSTRAccountMonitoringByAgreementNoThunk,
  disableSTRAccountMonitoringThunk,
  enableSTRAccountMonitoringThunk,
  disableSTRAccountRulesThunk,
  enableSTRAccountRulesThunk,
  unflagSTRAccountThunk,
  getSTRAccountFlagsThunk,
  downloadSTRExcelReportThunk,
  sendSTRExcelEmailThunk,
  sendSTRDailySummaryEmailThunk,
} from '../../services/strService/strThunk';

export interface STRState {
  // Dashboard
  dashboardStats: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRDashboardStats | null;
  };

  // Daily Summary
  dailySummary: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRDailySummary | null;
  };

  // Weekly Summary
  weeklySummary: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRDailySummary[];
  };

  // Rules
  rules: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRRule[];
  };

  // Selected Rule
  selectedRule: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRRule | null;
  };

  // Alerts
  alerts: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRAlert[];
    total: number;
  };

  // Alert Statistics
  alertStatistics: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: any;
  };

  // Account Monitoring
  accountMonitoring: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRAccountMonitoring | null;
  };

  // Account Flags
  accountFlags: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: STRAccountFlags | null;
  };

  // UI State
  ui: {
    selectedDateRange: [string, string] | null;
    selectedFilters: {
      severity?: string;
      status?: string;
      ruleCode?: string;
      agreementId?: string;
    };
    pagination: {
      current: number;
      pageSize: number;
    };
  };
}

const initialState: STRState = {
  dashboardStats: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  dailySummary: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  weeklySummary: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: [],
  },
  rules: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: [],
  },
  selectedRule: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  alerts: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: [],
    total: 0,
  },
  alertStatistics: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  accountMonitoring: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  accountFlags: {
    isLoading: false,
    isError: false,
    errorMessage: null,
    data: null,
  },
  ui: {
    selectedDateRange: null,
    selectedFilters: {},
    pagination: {
      current: 1,
      pageSize: 20,
    },
  },
};

const strSlice = createSlice({
  name: 'str',
  initialState,
  reducers: {
    // UI Actions
    setSelectedDateRange: (state, action: PayloadAction<[string, string] | null>) => {
      state.ui.selectedDateRange = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<Partial<STRState['ui']['selectedFilters']>>
    ) => {
      state.ui.selectedFilters = { ...state.ui.selectedFilters, ...action.payload };
    },
    setPagination: (state, action: PayloadAction<{ current: number; pageSize: number }>) => {
      state.ui.pagination = action.payload;
    },
    clearSelectedRule: (state) => {
      state.selectedRule = {
        isLoading: false,
        isError: false,
        errorMessage: null,
        data: null,
      };
    },
    clearAccountMonitoring: (state) => {
      state.accountMonitoring = {
        isLoading: false,
        isError: false,
        errorMessage: null,
        data: null,
      };
    },
    clearAccountFlags: (state) => {
      state.accountFlags = {
        isLoading: false,
        isError: false,
        errorMessage: null,
        data: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Dashboard Stats
    builder
      .addCase(getSTRDashboardStatsThunk.pending, (state) => {
        state.dashboardStats.isLoading = true;
        state.dashboardStats.isError = false;
        state.dashboardStats.errorMessage = null;
      })
      .addCase(getSTRDashboardStatsThunk.fulfilled, (state, action) => {
        state.dashboardStats.isLoading = false;
        state.dashboardStats.data = action.payload;
      })
      .addCase(getSTRDashboardStatsThunk.rejected, (state, action) => {
        state.dashboardStats.isLoading = false;
        state.dashboardStats.isError = true;
        state.dashboardStats.errorMessage = action.error.message;
      });

    // Daily Summary
    builder
      .addCase(getSTRDailySummaryThunk.pending, (state) => {
        state.dailySummary.isLoading = true;
        state.dailySummary.isError = false;
        state.dailySummary.errorMessage = null;
      })
      .addCase(getSTRDailySummaryThunk.fulfilled, (state, action) => {
        state.dailySummary.isLoading = false;
        state.dailySummary.data = action.payload;
      })
      .addCase(getSTRDailySummaryThunk.rejected, (state, action) => {
        state.dailySummary.isLoading = false;
        state.dailySummary.isError = true;
        state.dailySummary.errorMessage = action.error.message;
      });

    // Weekly Summary
    builder
      .addCase(getSTRWeeklySummaryThunk.pending, (state) => {
        state.weeklySummary.isLoading = true;
        state.weeklySummary.isError = false;
        state.weeklySummary.errorMessage = null;
      })
      .addCase(getSTRWeeklySummaryThunk.fulfilled, (state, action) => {
        state.weeklySummary.isLoading = false;
        state.weeklySummary.data = action.payload;
      })
      .addCase(getSTRWeeklySummaryThunk.rejected, (state, action) => {
        state.weeklySummary.isLoading = false;
        state.weeklySummary.isError = true;
        state.weeklySummary.errorMessage = action.error.message;
      });

    // Rules
    builder
      .addCase(getAllSTRRulesThunk.pending, (state) => {
        state.rules.isLoading = true;
        state.rules.isError = false;
        state.rules.errorMessage = null;
      })
      .addCase(getAllSTRRulesThunk.fulfilled, (state, action) => {
        state.rules.isLoading = false;
        state.rules.data = action.payload;
      })
      .addCase(getAllSTRRulesThunk.rejected, (state, action) => {
        state.rules.isLoading = false;
        state.rules.isError = true;
        state.rules.errorMessage = action.error.message;
      });

    // Selected Rule
    builder
      .addCase(getSTRRuleThunk.pending, (state) => {
        state.selectedRule.isLoading = true;
        state.selectedRule.isError = false;
        state.selectedRule.errorMessage = null;
      })
      .addCase(getSTRRuleThunk.fulfilled, (state, action) => {
        state.selectedRule.isLoading = false;
        state.selectedRule.data = action.payload;
      })
      .addCase(getSTRRuleThunk.rejected, (state, action) => {
        state.selectedRule.isLoading = false;
        state.selectedRule.isError = true;
        state.selectedRule.errorMessage = action.error.message;
      });

    // Update Rule Thresholds
    builder.addCase(updateSTRRuleThresholdsThunk.fulfilled, (state, action) => {
      // Update the rule in the rules array
      const ruleIndex = state.rules.data.findIndex(
        (rule) => rule.ruleCode === action.payload.ruleCode
      );
      if (ruleIndex !== -1) {
        // Update the rule data (this is a simplified update, you might need to fetch the rule again)
        state.rules.data[ruleIndex] = { ...state.rules.data[ruleIndex] };
      }
    });

    // Toggle Rule
    builder.addCase(toggleSTRRuleThunk.fulfilled, (state, action) => {
      const ruleIndex = state.rules.data.findIndex(
        (rule) => rule.ruleCode === action.payload.ruleCode
      );
      if (ruleIndex !== -1) {
        state.rules.data[ruleIndex].isEnabled = action.payload.isEnabled;
      }
    });

    // Alerts
    builder
      .addCase(getSTRAlertsThunk.pending, (state) => {
        state.alerts.isLoading = true;
        state.alerts.isError = false;
        state.alerts.errorMessage = null;
      })
      .addCase(getSTRAlertsThunk.fulfilled, (state, action) => {
        state.alerts.isLoading = false;
        state.alerts.data = action.payload.data;
        state.alerts.total = action.payload.pagination.total;
      })
      .addCase(getSTRAlertsThunk.rejected, (state, action) => {
        state.alerts.isLoading = false;
        state.alerts.isError = true;
        state.alerts.errorMessage = action.error.message;
      });

    // Hide Alert
    builder.addCase(hideSTRAlertThunk.fulfilled, (state, action) => {
      const alertIndex = state.alerts.data.findIndex((alert) => alert.id === action.payload);
      if (alertIndex !== -1) {
        state.alerts.data[alertIndex].isHidden = true;
        state.alerts.data[alertIndex].status = 'HIDDEN';
      }
    });

    // Show Alert
    builder.addCase(showSTRAlertThunk.fulfilled, (state, action) => {
      const alertIndex = state.alerts.data.findIndex((alert) => alert.id === action.payload);
      if (alertIndex !== -1) {
        state.alerts.data[alertIndex].isHidden = false;
        state.alerts.data[alertIndex].status = 'ACTIVE';
      }
    });

    // Resolve Alert
    builder.addCase(resolveSTRAlertThunk.fulfilled, (state, action) => {
      const alertIndex = state.alerts.data.findIndex((alert) => alert.id === action.payload);
      if (alertIndex !== -1) {
        state.alerts.data[alertIndex].status = 'RESOLVED';
        state.alerts.data[alertIndex].resolvedAt = new Date().toISOString();
      }
    });

    // Bulk Hide Alerts
    builder.addCase(bulkHideSTRAlertsThunk.fulfilled, (state, action) => {
      action.payload.forEach((alertId) => {
        const alertIndex = state.alerts.data.findIndex((alert) => alert.id === alertId);
        if (alertIndex !== -1) {
          state.alerts.data[alertIndex].isHidden = true;
          state.alerts.data[alertIndex].status = 'HIDDEN';
        }
      });
    });

    // Alert Statistics
    builder
      .addCase(getSTRAlertStatisticsThunk.pending, (state) => {
        state.alertStatistics.isLoading = true;
        state.alertStatistics.isError = false;
        state.alertStatistics.errorMessage = null;
      })
      .addCase(getSTRAlertStatisticsThunk.fulfilled, (state, action) => {
        state.alertStatistics.isLoading = false;
        state.alertStatistics.data = action.payload;
      })
      .addCase(getSTRAlertStatisticsThunk.rejected, (state, action) => {
        state.alertStatistics.isLoading = false;
        state.alertStatistics.isError = true;
        state.alertStatistics.errorMessage = action.error.message;
      });

    // Account Monitoring
    builder
      .addCase(getSTRAccountMonitoringStatusThunk.pending, (state) => {
        state.accountMonitoring.isLoading = true;
        state.accountMonitoring.isError = false;
        state.accountMonitoring.errorMessage = null;
      })
      .addCase(getSTRAccountMonitoringStatusThunk.fulfilled, (state, action) => {
        state.accountMonitoring.isLoading = false;
        state.accountMonitoring.data = action.payload;
      })
      .addCase(getSTRAccountMonitoringStatusThunk.rejected, (state, action) => {
        state.accountMonitoring.isLoading = false;
        state.accountMonitoring.isError = true;
        state.accountMonitoring.errorMessage = action.error.message;
      })
      .addCase(searchSTRAccountMonitoringByAgreementNoThunk.pending, (state) => {
        state.accountMonitoring.isLoading = true;
        state.accountMonitoring.isError = false;
        state.accountMonitoring.errorMessage = null;
      })
      .addCase(searchSTRAccountMonitoringByAgreementNoThunk.fulfilled, (state, action) => {
        state.accountMonitoring.isLoading = false;
        state.accountMonitoring.data = action.payload;
      })
      .addCase(searchSTRAccountMonitoringByAgreementNoThunk.rejected, (state, action) => {
        state.accountMonitoring.isLoading = false;
        state.accountMonitoring.isError = true;
        state.accountMonitoring.errorMessage = action.error.message;
      });

    // Account Flags
    builder
      .addCase(getSTRAccountFlagsThunk.pending, (state) => {
        state.accountFlags.isLoading = true;
        state.accountFlags.isError = false;
        state.accountFlags.errorMessage = null;
      })
      .addCase(getSTRAccountFlagsThunk.fulfilled, (state, action) => {
        state.accountFlags.isLoading = false;
        state.accountFlags.data = action.payload;
      })
      .addCase(getSTRAccountFlagsThunk.rejected, (state, action) => {
        state.accountFlags.isLoading = false;
        state.accountFlags.isError = true;
        state.accountFlags.errorMessage = action.error.message;
      });
  },
});

export const {
  setSelectedDateRange,
  setSelectedFilters,
  setPagination,
  clearSelectedRule,
  clearAccountMonitoring,
  clearAccountFlags,
} = strSlice.actions;

export default strSlice.reducer;
